import { useMsal } from '@azure/msal-react';
import React, { useEffect, useState } from 'react';
import Typewriter from 'typewriter-effect';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { cn } from '../../libs/utils/TailwindMerge';
import { useTheme } from '../../theme/ThemeContext';
import { AnimatedBeamGraphLogin } from '../Effects/AnimatedBeamGraph';
import { AnimatedGridPattern } from '../Effects/AnimatedGridPattern';
import { RainbowButton } from '../Effects/RainbowButton';
import { authentication as teamsAuth } from '@microsoft/teams-js';
import { useTeams } from '@microsoft/teamsfx-react';
import CacheUtils from '../../libs/utils/CacheUtils';
import { getErrorDetails } from '../utils/TextUtils';
import { LoadingMessage } from '../AppContainer/ChatView/ChatWindow/Shared/LoadingMessage';

const botName: string = import.meta.env.VITE_APP_BOT_NAME;

export const Login: React.FC = () => {
    const { instance } = useMsal();
    const { theme } = useTheme();
    const [isLoggingIn, setIsLoggingIn] = useState(true);
    const [firstRenderComplete, setFirstRenderComplete] = useState(false);

    if (theme === 'dark') {
        document.body.classList.add('dark');
    } else {
        document.body.classList.remove('dark');
    }

    useEffect(() => {
        if (localStorage.getItem('isLoggingIn') === 'true' || window.location.href.includes('code=')) {
            setIsLoggingIn(true);
        } else {
            setIsLoggingIn(false);
        }
        setFirstRenderComplete(true);
    }, []);

    const { inTeams, context } = useTeams()[0];

    const [TryingSSO, setTryingSSO] = React.useState(true);

    const tryTeamsAuth = async () => {
        teamsAuth
            .authenticate({
                url: window.location.origin
            })
            .then(() => {
                window.location.reload();
            })
            .catch((e: unknown) => {
                alert(`Error signing in: ${getErrorDetails(e)}`);
                // console.log('login.tsx: Error signing in:', getErrorDetails(e));
            });
        setTryingSSO(false);
    };

    useEffect(() => {
        const loginAttempted: boolean | null = CacheUtils.getStoredDataByKey('autoRedirectLoginAttempted', true) as
            | boolean
            | null;
        setTryingSSO(true);

        // const isTeamsInit = teamsApp.isInitialized();

        if (inTeams) {
            // If we are in teams and in an iframe, try to do SSO silent
            if (window.parent !== window) {
                teamsAuth
                    .getAuthToken()
                    .then(() => {
                        AuthHelper.ssoSilentRequestWithLoginHint(instance, context?.user?.loginHint)
                            .then((result) => {
                                instance.setActiveAccount(result.account);
                            })
                            .catch(() => {
                                tryTeamsAuth();
                            });
                    })
                    .catch(() => {
                        tryTeamsAuth();
                    });
            } else if (loginAttempted === false || loginAttempted === null) {
                // if we are in teams but not an iframe we are only trying to auth
                CacheUtils.setStoredDataByKey('autoRedirectLoginAttempted', true, 120, true);
                AuthHelper.loginRedirectRequestWithLoginHint(instance, context?.user?.loginHint).catch((e: unknown) => {
                    teamsAuth.notifyFailure(getErrorDetails(e));
                });
            } else {
                setTryingSSO(false);
            }
        } else {
            setTryingSSO(false);
        }
    }, [inTeams, instance, context]);

    const handleLoginClicked = () => {
        if (window.parent !== window && inTeams) {
            teamsAuth
                .authenticate({
                    url: window.location.origin,
                })
                .then(() => {
                    window.location.reload();
                    //instance.setActiveAccount(result);
                })
                .catch((e: unknown) => {
                    alert(`Error signing in: ${getErrorDetails(e)}`);
                });
        } else {
            try {
                setIsLoggingIn(true);
                localStorage.setItem('isLoggingIn', 'true');

                //try logging in silently
                const silentRequest = {
                    account: instance.getActiveAccount() ?? undefined,
                    scopes: AuthHelper.getMsalScopes(),
                };

                instance
                    .ssoSilent(silentRequest)
                    .then((response) => {
                        instance.setActiveAccount(response.account);
                        setIsLoggingIn(false);
                    })
                    .catch(() => {
                        //if silent login fails, redirect to login
                        instance.loginRedirect().catch((e) => {
                            console.error(e);
                            setIsLoggingIn(true);
                        });
                    });
            } catch {
                //if silent login fails, redirect to login
                instance.loginRedirect().catch((e) => {
                    console.error(e);
                    setIsLoggingIn(true);
                });
            }
        }
    };

    return (
        TryingSSO ? (
            <LoadingMessage message={'Trying to sign in you in automatically'} />
        ) : (
            <div className="flex h-screen p-0 m-0">
                <div className="relative w-full flex-col h-screen w-full justify-center overflow-hidden bg-slate-100 dark:bg-slate-900 p-20">
                    {!isLoggingIn && (
                        <div className="animate-fadeInSlow">
                            <h1 className="text-center whitespace-pre-wrap mb-10 text-5xl tracking-tighter bg-gradient-to-br from-primary-600 via-primary-300 to-primary-200 text-transparent bg-clip-text leading-loose">
                                {botName}
                            </h1>
                            <div className={`text-3xl text-center tracking-tighter text-primary-200`}>
                                <Typewriter
                                    options={{ loop: false }}
                                    onInit={(typewriter) => {
                                        typewriter.changeDelay(100).typeString('AI for your workplace.').start();
                                    }}
                                />
                            </div>
                            <RainbowButton
                                className="absolute z-[200] top-2/3 left-1/2 transform -translate-x-1/2 text-slate-100 dark:text-slate-800 hover:text-primary-300"
                                onClick={() => {
                                    handleLoginClicked();
                                }}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    id="uuid-f8d4d392-7c12-4bd9-baff-66fbf7814b91"
                                    className="h-5 w-5 mr-1"
                                    viewBox="0 0 18 18">
                                    <path
                                        d="m3.802,14.032c.388.242,1.033.511,1.715.511.621,0,1.198-.18,1.676-.487,0,0,.001,0,.002-.001l1.805-1.128v4.073c-.286,0-.574-.078-.824-.234l-4.374-2.734Z"
                                        fill="#225086"
                                    />
                                    <path
                                        d="m7.853,1.507L.353,9.967c-.579.654-.428,1.642.323,2.111,0,0,2.776,1.735,3.126,1.954.388.242,1.033.511,1.715.511.621,0,1.198-.18,1.676-.487,0,0,.001,0,.002-.001l1.805-1.128-4.364-2.728,4.365-4.924V1s0,0,0,0c-.424,0-.847.169-1.147.507Z"
                                        fill="#6df"
                                    />
                                    <polygon
                                        points="4.636 10.199 4.688 10.231 9 12.927 9.001 12.927 9.001 12.927 9.001 5.276 9 5.275 4.636 10.199"
                                        fill="#cbf8ff"
                                    />
                                    <path
                                        d="m17.324,12.078c.751-.469.902-1.457.323-2.111l-4.921-5.551c-.397-.185-.842-.291-1.313-.291-.925,0-1.752.399-2.302,1.026l-.109.123h0s4.364,4.924,4.364,4.924h0s0,0,0,0l-4.365,2.728v4.073c.287,0,.573-.078.823-.234l7.5-4.688Z"
                                        fill="#074793"
                                    />
                                    <path
                                        d="m9.001,1v4.275s.109-.123.109-.123c.55-.627,1.377-1.026,2.302-1.026.472,0,.916.107,1.313.291l-2.579-2.909c-.299-.338-.723-.507-1.146-.507Z"
                                        fill="#0294e4"
                                    />
                                    <polygon
                                        points="13.365 10.199 13.365 10.199 13.365 10.199 9.001 5.276 9.001 12.926 13.365 10.199"
                                        fill="#96bcc2"
                                    />
                                </svg>
                                Login <span className="hidden sm:flex ml-1">with Single Sign-On</span>
                            </RainbowButton>
                        </div>
                    )}

                    {isLoggingIn && firstRenderComplete && (
                        <div>
                            <AnimatedBeamGraphLogin
                                className="hidden sm:flex absolute z-[200] top-20 mt-10 left-1/2 transform -translate-x-1/2"
                                theme={theme}
                            />
                        </div>
                    )}
                </div>
                <AnimatedGridPattern
                    numSquares={30}
                    maxOpacity={0.1}
                    duration={3}
                    repeatDelay={1}
                    className={cn(
                        '[mask-image:radial-gradient(500px_circle_at_center,white,transparent)]',
                        'inset-x-0 inset-y-[-30%] h-[200%] skew-y-12',
                    )}
                />
            </div>
        )
    );
};
